import { OnboardingPlaceholderIcon } from '#app/__generated__/components/Icons/OnboardingPlaceholder.js';
import { Text } from '#app/components/Primitives/Text.js';
import { ThemeColorsV2 } from '#app/theme/index.js';
import { type BaseAvatarProps } from './types.js';
import { getAvatarSize } from './utils.js';
import { forwardRef, type Ref } from 'react';
import { css, styled, useTheme } from 'styled-components';

const shouldUseBlackText = (color: ThemeColorsV2) => {
  return ['brandBlue', 'brandLilac', 'brandYellow', 'gray10', 'white'].includes(
    color,
  );
};

const StyledAvatarPlaceholder = styled.div<{
  fallbackColor?: ThemeColorsV2;
  hasFallbackName: boolean;
  height: number;
  hidePlaceholderOverflow?: boolean;
  width: number;
}>`
  ${({
    fallbackColor,
    hasFallbackName,
    height,
    hidePlaceholderOverflow = false,
    theme,
    width,
  }) => css`
    border-radius: 50%;
    height: ${height}px;
    width: ${width}px;

    ${hasFallbackName &&
    css`
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      background-color: ${fallbackColor
        ? theme.colorsV2[fallbackColor]
        : theme.colorsV2.brandPurple};

      p {
        color: ${fallbackColor
          ? shouldUseBlackText(fallbackColor)
            ? theme.colorsV2.gray80
            : theme.colorsV2.white
          : theme.colorsV2.white};
        line-height: 0;
      }
    `}

    ${hidePlaceholderOverflow
      ? css`
          overflow: hidden;
        `
      : undefined}
  `}
`;

export const AvatarPlaceholder = forwardRef(
  (
    {
      fallbackColor,
      fallbackInitialsLength,
      fallbackInitialsTextStyle,
      fallbackName,
      size = 'xs',
      ...props
    }: BaseAvatarProps,
    ref: Ref<HTMLImageElement>,
  ) => {
    const computedSize = getAvatarSize(size);
    const theme = useTheme();

    const fallbackNameInitials =
      fallbackName
        ?.trimStart()
        ?.split(' ')
        .map((part) => part.charAt(0)) ?? [];
    // Set the length if specified or fallback to the first character
    const fallbackInitials = (
      fallbackInitialsLength
        ? fallbackNameInitials.slice(0, fallbackInitialsLength).join('')
        : fallbackNameInitials[0]
    )?.toUpperCase();

    const fallBackTextStyle = fallbackInitialsTextStyle
      ? fallbackInitialsTextStyle
      : typeof size === 'number'
        ? 'captionSmall'
        : size === 'lg' || size === 'xl' || size === 'xxl'
          ? 'subtitleRegular'
          : 'subtitleSmall';

    return (
      <StyledAvatarPlaceholder
        aria-label="avatar placeholder"
        role="presentation"
        {...props}
        fallbackColor={fallbackColor}
        hasFallbackName={Boolean(fallbackInitials)}
        height={computedSize}
        ref={ref}
        width={computedSize}
      >
        {fallbackInitials ? (
          <Text
            color={theme.colorsV2.white}
            textStyle={fallBackTextStyle}
          >
            {fallbackInitials}
          </Text>
        ) : (
          <OnboardingPlaceholderIcon size="100%" />
        )}
      </StyledAvatarPlaceholder>
    );
  },
);
